import Immutable from 'immutable'
import template from 'lodash.template'
import * as Sentry from '@sentry/browser'
import { FORMATTERS, MULTIVALUE_FORMATTERS, MULTIVALUE_INPUT_TYPES } from './Constants'
import { stripExtraSpace } from '../../../../utils'
import { SomeImmutableJsThing } from './types'

const INTERPOLATE_REGEX = /{{([\s\S]+?)}}/g
const MATCH_ALL_VARS = /{{([\s\S]+?)}}/g
const MATCH_BRACKETS = /{{|}}/g

/*
 *  Compile various elements of the dynamic form
 *  @formatFormValues {Bool} whether to apply formatter to form values. Set to true for rendering body and title, false when
 *  rendering form components so hidden and required fields logic is handled correctly.
 */
const compiler = (value, form: SomeImmutableJsThing, shouldStripExtraSpace = false, formatFormValues = true) => {
  if (!value) return value
  let localValue = value

  try {
    // Rich text editors may transform the following characters into HTML entities
    // eslint-disable-next-line no-irregular-whitespace
    // For example: {{ 1 < 2 && 4 > 3 && 5 <= 5  }} == <p>{{ 1 &lt; 2 &amp;&amp; 4 &gt; 3 &amp;&amp; 5 &lt;= 5&nbsp; }}</p>
    // Decode common HTML entities back to their original characters
    localValue = value
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&nbsp;/g, ' ')

    const engine = template(localValue, { interpolate: INTERPOLATE_REGEX })

    const defaults = (localValue.match(MATCH_ALL_VARS) || []).map(v => [v.replace(MATCH_BRACKETS, ''), ''])

    const variables = form.reduce((accumulator, formField) => {
      const props = formField.get('props')
      let formattedValue = props.get('value')
      if (formatFormValues) {
        if (MULTIVALUE_INPUT_TYPES.includes(formField.get('type'))) {
          const specifiedFormatter = MULTIVALUE_FORMATTERS[props.get('formatter')]
          const formatter = specifiedFormatter || MULTIVALUE_FORMATTERS.commaSeparated

          // When this is called on
          // - the admin page http://localhost:3000/admin/templates/9/edit
          // - the listing workflow http://localhost:3000/products/new
          // formattedValue is a JS array, despite existing inside a big ImmutableJS thing (see sortValuesAccordingToOriginalAsJS).
          //
          // When this is called on express list
          // (which maintains the state as plain JS things and calls Immutable.fromJS before passing them in)
          // formattedValue is an ImmutableJS thing.
          //
          // The call to formatter(formattedValue) needs a plain JS object (array)
          if (typeof formattedValue.toJS === 'function') {
            formattedValue = formattedValue.toJS()
          }

          formattedValue = formatter(formattedValue)
        } else {
          try {
            formattedValue = FORMATTERS[props.get('formatter') || 'none'](formattedValue)
          } catch (e) {
            Sentry.captureException(e)
            // This will happen if you configure a Title Case formatter on the builder template field, then have it populate from a numeric draft product field (like height).
            // eslint-disable-next-line no-console
            console.log(`Could not apply formatter ${props.get('formatter')}. Using value of ${formattedValue}`)
          }
        }
      }
      return accumulator.set(formField.get('variableName'), formattedValue)
    }, Immutable.Map(defaults))

    const out = engine(variables.toJS())

    if (shouldStripExtraSpace) {
      return stripExtraSpace(out)
    }

    return out
  } catch (error) {
    // You'll get lots of noise if you do this - any time someone has some invalid JS in the "hidden" field for an input.
    // Sentry.captureException(error)

    // eslint-disable-next-line no-console
    console.log(error.message)
    return localValue
  }
}

export default compiler
